import { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { changeComposeNotifyMembers } from 'mastodon/actions/compose';
import type { ApiCommunityJSON } from 'mastodon/api_types/communities';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

// Tiny inline checkbox shown above the submit button when:
//  - the compose audience is a community
//  - the current viewer is a mod or admin of that community
// When checked, every member of the community receives a community_post
// notification on submit. Default unchecked.
export const NotifyMembersToggle: React.FC = () => {
  const dispatch = useAppDispatch();

  const communityId = useAppSelector(
    (s) => s.compose.get('community_id') as string | null,
  );
  const checked = useAppSelector((s) => !!s.compose.get('notify_members'));

  const community = useAppSelector((s) => {
    if (!communityId) return null;
    const byId = (s.communities as { byId: Record<string, ApiCommunityJSON> })
      .byId;
    return byId[communityId] ?? null;
  });

  const isModerator = community
    ? community.viewer_role === 'admin' || community.viewer_role === 'mod'
    : false;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(changeComposeNotifyMembers(e.target.checked));
    },
    [dispatch],
  );

  if (!community || !isModerator) return null;

  return (
    <label className='compose-form__notify-members'>
      <input type='checkbox' checked={checked} onChange={handleChange} />
      <span>
        <FormattedMessage
          id='compose_form.notify_members'
          defaultMessage='Notify all {count, plural, one {# member} other {# members}} of {name}'
          values={{ count: community.members_count ?? 0, name: community.name }}
        />
      </span>
    </label>
  );
};
